/**
 * Custom element for the "Pairs well with" variant-aware carousel.
 *
 * Listens for Horizon's `variant:update` event on the nearest `.shopify-section`
 * parent. When the event fires, the updated section HTML is available in
 * `event.detail.data.html`. We find our counterpart element by `data-block-id`
 * and swap our inner HTML in-place — the same pattern used by `dev-omnibus-price`
 * and the theme's own `product-price` component.
 *
 * The `hidden` attribute is managed server-side (Liquid) on initial render and
 * preserved here after swaps so the element stays invisible when the new variant
 * has no pairs metafield data.
 */
class DevPairsWellWith extends HTMLElement {
  connectedCallback() {
    this._section = this.closest('.shopify-section, dialog');
    if (!this._section) return;
    this._section.addEventListener('variant:update', this._onVariantUpdate);
  }

  disconnectedCallback() {
    if (!this._section) return;
    this._section.removeEventListener('variant:update', this._onVariantUpdate);
  }

  _onVariantUpdate = (/** @type {CustomEvent} */ event) => {
    const html = event.detail?.data?.html;
    if (!html) return;

    // When a combined listing loads a new product, update our tracked product ID.
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (
      event.target instanceof HTMLElement &&
      event.target.dataset.productId !== this.dataset.productId
    ) {
      return;
    }

    const updated = html.querySelector(
      `dev-pairs-well-with[data-block-id="${this.dataset.blockId}"]`
    );
    if (!updated) return;

    // Sync visibility before swapping content so there's no layout flash.
    if (updated.hasAttribute('hidden')) {
      this.setAttribute('hidden', '');
    } else {
      this.removeAttribute('hidden');
    }

    this.innerHTML = updated.innerHTML;
  };

}

if (!customElements.get('dev-pairs-well-with')) {
  customElements.define('dev-pairs-well-with', DevPairsWellWith);
}
